/**
 * Menu Item Row Component
 * Single row for displaying menu item in list view
 */

import { Edit2, Package, Trash2 } from 'lucide-react';
import React from 'react';
import { MenuItem } from '../../../../types/menu';
import { Button } from '../../../common';

interface MenuItemRowProps {
  canManageMenu?: boolean;
  item: MenuItem;
  selectedOutletName?: string | null;
  onEdit: (item: MenuItem) => void;
  onDelete: (item: MenuItem) => void;
  formatPrice: (price: number) => string;
}

const ItemStatusBadge: React.FC<{ status: MenuItem['status'] }> = ({
  status,
}) => {
  const statusStyles = {
    active: 'bg-green-100 text-green-800',
    out_of_stock: 'bg-amber-100 text-amber-800',
    inactive: 'bg-gray-100 text-gray-800',
  };

  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase ${statusStyles[status]}`}
    >
      {status === 'out_of_stock' ? 'OOS' : status}
    </span>
  );
};

const ItemThumbnail: React.FC<{ imageUrl?: string }> = ({ imageUrl }) => (
  <div className="w-8 h-8 rounded bg-gray-100 flex-shrink-0 overflow-hidden">
    {imageUrl ? (
      <img src={imageUrl} className="w-full h-full object-cover" alt="" />
    ) : (
      <Package size={16} className="m-auto text-gray-400" />
    )}
  </div>
);

export const MenuItemRow: React.FC<MenuItemRowProps> = ({
  canManageMenu = true,
  item,
  selectedOutletName,
  onEdit,
  onDelete,
  formatPrice,
}) => {
  return (
    <div className="grid grid-cols-12 gap-4 px-6 py-3 items-center hover:bg-gray-50 transition-colors">
      <div className="col-span-3 flex items-center gap-3">
        <ItemThumbnail imageUrl={item.image_url} />
        <div className="min-w-0">
          <p className="font-medium text-gray-900 text-sm truncate">
            {item.name}
          </p>
          <p className="text-xs text-gray-500 truncate">{item.description}</p>
        </div>
      </div>
      <div className="col-span-2 text-sm text-gray-600 truncate">
        {item.outlet_name || selectedOutletName || '-'}
      </div>
      <div className="col-span-2 text-sm text-gray-600 truncate">
        {item.category_name || '-'}
      </div>
      <div className="col-span-2 text-sm text-gray-600 truncate">
        {item.department_name || '-'}
      </div>
      <div className="col-span-1 text-sm font-medium text-gray-900">
        {formatPrice(item.base_price)}
      </div>
      <div className="col-span-1">
        <ItemStatusBadge status={item.status} />
      </div>
      <div className="col-span-1 flex items-center justify-end gap-1">
        {canManageMenu ? (
          <>
            <Button
              onClick={() => onEdit(item)}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            >
              <Edit2 size={14} />
            </Button>
            <Button
              onClick={() => onDelete(item)}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded"
            >
              <Trash2 size={14} />
            </Button>
          </>
        ) : null}
      </div>
    </div>
  );
};
